import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface GpsDevice {
  id: number
  name: string
  serial_number: string | null
  current_user_id: number | null
  current_user: string | null
  start_date: string | null
  end_date: string | null
  status: number
  log: string | null
  sort_order: number | null
  notes: string | null
  available: boolean
  assigned: boolean
  created_at: string
  updated_at: string
  assignments_count: number
}

interface Assignment {
  id: number
  case_id: number
  case_name: string | null
  installed_at: string | null
  installed_by: string | null
  install_vehicle: string | null
  install_gps_location: string | null
  install_vehicle_location: string | null
  removed_at: string | null
  removed_by: string | null
  remove_vehicle_location: string | null
  returned_at: string | null
  returned_by: string | null
  status: number
}

interface Props {
  gps_device: GpsDevice
  recent_assignments: Assignment[]
}

export default function Show({ gps_device, recent_assignments }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "GPS Devices", href: "/gps_devices" },
    { title: gps_device.name, href: `/gps_devices/${gps_device.id}` },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this GPS device?")) {
      router.delete(`/gps_devices/${gps_device.id}`)
    }
  }

  const handleUnassign = () => {
    if (confirm("Are you sure you want to unassign this GPS device?")) {
      router.patch(`/gps_devices/${gps_device.id}/unassign`)
    }
  }

  const formatDateTime = (value: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString()
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={gps_device.name} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{gps_device.name}</h1>
          <div className="space-x-2">
            <Link
              href={`/gps_devices/${gps_device.id}/edit`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Edit
            </Link>
            {gps_device.assigned && (
              <button
                onClick={handleUnassign}
                className="bg-hpi-yellow text-hpi-dark-gray hover:bg-hpi-yellow/80 rounded px-4 py-2"
              >
                Unassign
              </button>
            )}
            <button
              onClick={handleDelete}
              className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
            >
              Delete
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Device Information</h2>
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-hpi-med-blue font-medium">Name</dt>
              <dd>{gps_device.name}</dd>
              <dt className="text-hpi-med-blue font-medium">Serial Number</dt>
              <dd>{gps_device.serial_number ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Status</dt>
              <dd>
                {gps_device.available ? (
                  <span className="text-hpi-dark-green">Available</span>
                ) : gps_device.assigned ? (
                  <span className="text-hpi-active-blue">Assigned</span>
                ) : (
                  <span className="text-hpi-med-blue">-</span>
                )}
              </dd>
              <dt className="text-hpi-med-blue font-medium">Sort Order</dt>
              <dd>{gps_device.sort_order ?? "-"}</dd>
            </dl>
          </div>

          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Assignment</h2>
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-hpi-med-blue font-medium">Current User</dt>
              <dd>{gps_device.current_user ?? "-"}</dd>
              <dt className="text-hpi-med-blue font-medium">Start Date</dt>
              <dd>{formatDateTime(gps_device.start_date)}</dd>
              <dt className="text-hpi-med-blue font-medium">End Date</dt>
              <dd>{formatDateTime(gps_device.end_date)}</dd>
            </dl>
          </div>

          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Metadata</h2>
            <dl className="grid grid-cols-2 gap-2">
              <dt className="text-hpi-med-blue font-medium">Created</dt>
              <dd>{formatDate(gps_device.created_at)}</dd>
              <dt className="text-hpi-med-blue font-medium">Updated</dt>
              <dd>{formatDate(gps_device.updated_at)}</dd>
            </dl>
          </div>
        </div>

        {gps_device.log && (
          <div className="border-hpi-light-gray mb-6 rounded border p-4">
            <h2 className="mb-2 text-lg font-semibold">Log</h2>
            <p className="whitespace-pre-wrap">{gps_device.log}</p>
          </div>
        )}

        {gps_device.notes && (
          <div className="border-hpi-light-gray mb-6 rounded border p-4">
            <h2 className="mb-2 text-lg font-semibold">Notes</h2>
            <p className="whitespace-pre-wrap">{gps_device.notes}</p>
          </div>
        )}

        <div className="border-hpi-light-gray rounded border p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Case Assignments ({gps_device.assignments_count})
            </h2>
            <Link
              href={`/gps_devices/${gps_device.id}/assignments`}
              className="text-hpi-active-blue hover:underline"
            >
              View All
            </Link>
          </div>

          {recent_assignments.length > 0 ? (
            <table className="border-hpi-light-gray w-full border-collapse border">
              <thead>
                <tr className="bg-hpi-pale-gray">
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Case
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Installed
                  </th>
                  <th className="border-hpi-light-gray border px-3 py-2 text-left">
                    Removed
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent_assignments.map((assignment) => (
                  <tr
                    key={assignment.id}
                    className="hover:bg-hpi-pale-blue align-top"
                  >
                    <td className="border-hpi-light-gray border px-3 py-2">
                      {assignment.case_id ? (
                        <Link
                          href={`/cases/${assignment.case_id}`}
                          className="text-hpi-active-blue hover:underline"
                        >
                          {assignment.case_name ?? `#${assignment.case_id}`}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      <div>
                        {formatDateTime(assignment.installed_at)}
                        {assignment.installed_by &&
                          ` by ${assignment.installed_by}`}
                      </div>
                      {assignment.install_vehicle && (
                        <div className="text-sm">
                          <span className="font-bold">Vehicle:</span>{" "}
                          {assignment.install_vehicle}
                        </div>
                      )}
                      {assignment.install_gps_location && (
                        <div className="text-sm">
                          <span className="font-bold">GPS Position:</span>{" "}
                          {assignment.install_gps_location}
                        </div>
                      )}
                      {assignment.install_vehicle_location && (
                        <div className="text-sm">
                          <span className="font-bold">Installed:</span>{" "}
                          {assignment.install_vehicle_location}
                        </div>
                      )}
                    </td>
                    <td className="border-hpi-light-gray border px-3 py-2">
                      {assignment.removed_at ? (
                        <>
                          <div>
                            {formatDateTime(assignment.removed_at)}
                            {assignment.removed_by &&
                              ` by ${assignment.removed_by}`}
                          </div>
                          {assignment.remove_vehicle_location && (
                            <div className="text-sm">
                              <span className="font-bold">Removed at:</span>{" "}
                              {assignment.remove_vehicle_location}
                            </div>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-hpi-med-gray">No assignments found.</p>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
